import './index.css';
import Main from "./pages/Main/Main";
import BottomBar from "./components/bottom_bar/BottomBar";
import useTelegram from "./hooks/useTelegram";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import i18n from "i18next";
import AuthAutoGate from "./components/customPlaceholders/AuthAutoGate";
import BottomBarGlass from './components/bottom_bar/BottomBarGlass';
import { KeyboardProvider, CustomKeyboard } from './components/Keyboard';
import PromosApi from "./api/PromosApi";
import { setSelectedPromo } from "./store/slices/selectedPromoSlice";
import { setPage, goBack } from "./store/slices/pageSlice";
import { handleBack } from "./utils/backHandler";
import { PromoLeaveGuardProvider, usePromoLeaveGuard } from "./context/PromoLeaveGuardContext";
import { clearPromoCreationState, isPromoFormPage } from "./utils/promoFormDraft";

function AppShell({ tg }) {
    const dispatch = useDispatch();
    const { requestLeave } = usePromoLeaveGuard();
    const currentPageId = useSelector((state) => state.page.currentPageId);
    const historyLength = useSelector((state) => state.page.history.length);
    const pageRef = useRef({ currentPageId, historyLength });

    useEffect(() => {
        pageRef.current = { currentPageId, historyLength };
    }, [currentPageId, historyLength]);

    useEffect(() => {
        if (!tg) return;
        const handle = () => {
            if (handleBack()) return;

            const { currentPageId: pid, historyLength: hLen } = pageRef.current;
            if (hLen === 0 && pid === 0) {
                tg.close();
                return;
            }

            if (isPromoFormPage(pid)) {
                requestLeave(() => {
                    clearPromoCreationState(dispatch);
                    dispatch(goBack());
                });
                return;
            }

            dispatch(goBack());
        };
        tg.BackButton.onClick(handle);
        return () => tg.BackButton.offClick(handle);
    }, [tg, dispatch, requestLeave]);

    return (
        <>
            <Main />
            <BottomBarGlass />
        </>
    );
}

function App() {
    console.log('start from sardor');
    const dispatch = useDispatch();
    const tg = useTelegram();
    const { startParam, refCode, promoId } = tg;
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userData, setUserData] = useState(null);
    const [promoLoaded, setPromoLoaded] = useState(false);
    
    // Debug: логирование реф-ссылки
    useEffect(() => {
        console.log('🔗 Referral Link Debug:');
        console.log('  startParam:', startParam || 'none');
        console.log('  refCode:', refCode || 'none');
        console.log('  promoId:', promoId || 'none');
    }, [startParam, refCode, promoId]);

    // Автооткрытие промо при переходе по реф-ссылке
    useEffect(() => {
        if (!isAuthenticated || !promoId || promoLoaded) return;
        
        console.log('📦 Loading promo from ref link, promoId:', promoId);
        
        PromosApi.getById(promoId)
            .then(promo => {
                console.log('✅ Promo loaded:', promo);
                if (promo) {
                    // Устанавливаем промо и переходим на страницу деталей
                    dispatch(setSelectedPromo({ ...promo, isSubscribed: false }));
                    dispatch(setPage({ id: 5, name: 'PromoDetails' }));
                    setPromoLoaded(true);
                }
            })
            .catch(err => {
                console.error('❌ Failed to load promo:', err);
            });
    }, [isAuthenticated, promoId, promoLoaded, dispatch]);

    // Восстановление приложения после сворачивания / переключения
    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log('🔄 App resumed (visibilitychange → visible)');
                const webApp = window.Telegram?.WebApp;
                if (!webApp || !webApp.initData) return;

                try { webApp.expand(); } catch (e) { /* ignore */ }
                const desktopPlatforms = ['tdesktop', 'macos', 'web', 'weba'];
                if (!desktopPlatforms.includes(webApp.platform) && typeof webApp.requestFullscreen === 'function') {
                    try { webApp.requestFullscreen(); } catch (e) { /* ignore */ }
                }
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, []);

    // Приложение всегда на английском; перевод только на странице Overview
    useEffect(() => {
        i18n.changeLanguage('en');
    }, []);

    // Обработчики авторизации
    const handleAuthSuccess = (user) => {
        console.log('✅ App.js: авторизация успешна, роль:', user?.role);
        console.log('🔄 App.js: Устанавливаем isAuthenticated = true');
        setUserData(user);
        setIsAuthenticated(true);
        console.log('🎯 App.js: Состояние обновлено, должен показаться Main и BottomBar');
    };

    const handleAuthFail = (error) => {
        console.error('❌ App.js: Ошибка авторизации:', error);
        // В случае ошибки все равно показываем приложение для разработки
        console.log('🔄 App.js: Fallback - показываем приложение для разработки');
        setIsAuthenticated(true);
    };

    return (
        <KeyboardProvider>
            <div className="flex flex-col w-screen h-screen overflow-hidden fixed bg-black text-white main-container">
                {!isAuthenticated ? (
                    <AuthAutoGate 
                        onSuccess={handleAuthSuccess}
                        onFail={handleAuthFail}
                    />
                ) : (
                    <PromoLeaveGuardProvider>
                        <AppShell tg={tg} />
                    </PromoLeaveGuardProvider>
                )}
                
                {/* Custom Keyboard - рендерится поверх всего */}
                <CustomKeyboard />
            </div>
        </KeyboardProvider>
    );
}

export default App;
